/**
 * 書込 outbox（純関数・node テスト可能）— Phase 2 / Slice 2
 * -----------------------------------------------------------------------------
 * クライアントは「楽観反映」だけを行い、裁定はサーバ（server/src/sync-core.mjs / D-0034）に委ねる。
 * ここは (1) 編集を行単位のドラフトに畳み込み (2) サーバ送信用ミューテーションへ変換
 * (3) サーバ結果を行へ反映（成功=version++／競合=サーバ現在値で上書き）する純関数群。
 *
 * ドラフト形式（1 行分の保留編集）:
 *   { values: {col:val}, base: {col:編集開始時の値}, baseVersion: number }
 *   - base は「編集を始めた時点でクライアントが見ていた値」。フィールド単位競合判定にサーバが使う。
 *   - base は各フィールド最初の編集時に一度だけ確定する（連続編集で基点がぶれない）。
 */

/** 編集をドラフトへ畳み込む。base は初回のみ確定（currentValue=楽観反映前の値）。 */
export function upsertDraft(draft, key, value, currentValue, baseVersion) {
  const d = draft ?? { values: {}, base: {}, baseVersion };
  if (!(key in d.base)) d.base = { ...d.base, [key]: currentValue };
  d.values = { ...d.values, [key]: value };
  return d;
}

/** ドラフト → サーバ送信ミューテーション（server data.ts の Mutation 互換）。 */
export function draftToMutation(rowId, draft, idempotencyKey) {
  return {
    op: "update",
    rowId,
    values: { ...draft.values },
    base: { ...draft.base },
    baseVersion: draft.baseVersion,
    idempotencyKey,
  };
}

/**
 * サーバ結果を行へ反映する。楽観反映は既に済んでいる前提。
 * @returns {{ row, status: "saved"|"conflict"|"error", conflicts?: string[], error?: string }}
 *  - ok            → version を +1（サーバは 1 ミューテーション=version+1）
 *  - conflict      → **サーバ権威**: サーバの現在値＋version で行を置換（楽観分は破棄・ユーザーが再編集）
 *  - not-found 等  → error（行はそのまま・呼び出し側で通知）
 */
export function reconcile(row, result) {
  if (result && result.ok) {
    return { row: { ...row, __version: (row.__version ?? 0) + 1 }, status: "saved" };
  }
  if (result && result.errorCode === "conflict") {
    return {
      row: { __rowId: row.__rowId, __version: result.version ?? row.__version, ...(result.current ?? {}) },
      status: "conflict",
      conflicts: result.conflicts ?? [],
    };
  }
  return { row, status: "error", error: (result && (result.error || result.errorCode)) || "unknown" };
}

/** ブラウザ/Node 双方で使える冪等キー生成。 */
export function newIdempotencyKey(seed) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${seed ?? ""}-${Math.round(performance?.now?.() ?? 0)}`;
}

// ============================================================================
// create / delete（Slice 2 完了分）
// ============================================================================

/** 行からメタ（__rowId/__version 等の __ 始まり）を除いた送信用 values を作る。 */
export function rowValues(row) {
  const out = {};
  for (const k of Object.keys(row ?? {})) {
    if (!k.startsWith("__")) out[k] = row[k];
  }
  return out;
}

/**
 * 新規行 → create ミューテーション（server data.ts doCreate 互換）。
 * サーバの create は冪等（同一 rowId の再送 = 成功扱い・値は更新しない）ため、
 * **最初の flush に行の全値を載せる**（以後の編集は update に切り替える）。
 */
export function createToMutation(rowId, row, idempotencyKey) {
  return { op: "create", rowId, values: rowValues(row), idempotencyKey };
}

/** 行削除 → delete ミューテーション。 */
export function deleteToMutation(rowId, idempotencyKey) {
  return { op: "delete", rowId, idempotencyKey };
}

/**
 * delete 結果の解釈。not-found は「既に無い」＝目的達成なので成功扱い。
 * @returns {{ status: "saved"|"error", error?: string }}
 */
export function reconcileDelete(result) {
  if (result && (result.ok || result.errorCode === "not-found")) return { status: "saved" };
  return { status: "error", error: (result && (result.error || result.errorCode)) || "unknown" };
}
